import { WarningCircleIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useCallback } from 'react'
import { useGetScanAndRepository } from '../../hooks/scan-hooks.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteScan } from '../../utils/api-client/api-hooks.ts'
import type { Repository, ScanResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { Spinner } from '../spinner.tsx'
import { BranchTag } from '../tags/branch-tag.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import * as styles from './delete-scan-modal.css.ts'

type DeleteScanModalProps = {
  scanId: string
  onClose: () => unknown
  onDeleteSuccess?: () => unknown
}

export const DeleteScanModal: React.FC<DeleteScanModalProps> = ({ scanId, onClose, onDeleteSuccess = () => {} }) => {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Unable to delete scan' })
  const { scan, isScanLoading, repository, isRepositoryLoading } = useGetScanAndRepository({ scanId })
  const { mutate: deleteScan, isPending } = useDeleteScan({
    onSuccess: () => {
      onDeleteSuccess()
      addToast({
        message: <>Scan deleted successfully</>,
        variant: 'success',
      })
    },
    onError: showMutationError,
  })
  const handleDeleteScan = useCallback(() => deleteScan({ scanId }), [deleteScan, scanId])

  if (isScanLoading || isRepositoryLoading) {
    return (
      <Dialog.Root open={true}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.alertDialogOverlay} />
          <Dialog.Content className={styles.alertDialogContent}>
            <Spinner size="24" label="Loading scan details..." />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  if (!scan || !repository) {
    return null
  }

  return (
    <DeleteScanModalPure
      scan={scan}
      repository={repository}
      onClose={onClose}
      onDeleteScan={handleDeleteScan}
      isDeleting={isPending}
    />
  )
}

type DeleteScanModalPureProps = {
  scan: Pick<ScanResponse, 'id' | 'detector' | 'imported_at' | 'branch'>
  repository: Pick<Repository, 'name' | 'type'>
  onClose: () => unknown
  onDeleteScan: () => unknown
  isDeleting?: boolean
}

export const DeleteScanModalPure: React.FC<DeleteScanModalPureProps> = ({
  scan,
  repository,
  onClose,
  onDeleteScan,
  isDeleting = false,
}) => {
  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.alertDialogOverlay} />
        <Dialog.Content className={styles.alertDialogContent}>
          <Dialog.Title className={styles.alertDialogTitle}>
            <WarningCircleIcon className={styles.alertDialogIcon} size={32} weight="fill" />
            Delete scan?
          </Dialog.Title>
          <Dialog.Description className={styles.alertDialogDescription} asChild>
            <div>
              <p className={styles.alertDialogDescriptionParagraph}>
                You are about to delete a scan from{' '}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', verticalAlign: 'middle' }}>
                  <SCMIcon type={repository.type} size="medium" />
                  <strong>{repository.name}</strong>
                </span>
              </p>
              <div className={styles.alertDialogRepository} style={{ gap: '16px' }}>
                <ToolIcon tool={scan.detector} size="large+" withAriaLabel={true} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                  <span>{formatDate({ date: scan.imported_at, type: 'mm/dd/yy' })}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontStyle: 'italic' }}>
                      {formatDate({ date: scan.imported_at, type: 'time-with-zone' })}
                    </span>
                    <BranchTag branch={scan.branch} />
                  </span>
                </div>
              </div>
              <p className={styles.alertDialogDescriptionParagraph}>
                This includes all of its associated analysis, and cannot be undone. Do you wish to proceed?
              </p>
            </div>
          </Dialog.Description>
          <div className={styles.alertDialogButtons}>
            <GhostButton type="secondary" onClick={onClose}>
              Cancel
            </GhostButton>
            <DefaultButton buttonType="destructive" onClick={onDeleteScan} state={isDeleting ? 'loading' : 'default'}>
              Delete scan
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
